import { Typography } from '@sweep-io/sweep-design';
import Stack from '@mui/material/Stack';
import { AgentPopoverCard } from './AgentPopoverCard';
import { emptyStateFlowMonitoring, emptyStateLeadMonitoring } from './agentsEmptyStates';
import { AgentType, MonitoringV2ContextComponent } from '@server/ai';
import useCreateAgent from '../../../aiAgentCenter/useCreateAgent';
import { useDocumentationApiFacade } from '../../../../apis/facades/useDocumentationApiFacade';
import { useBestPracticesContent } from '../../../bestPractices/useBestPracticesContent';
import { useMonitoringAgentHomepageContext } from './MonitoringAgentsLoadingContext';

interface MonitoringAgentsPopoverEmptyStateProps {
  crmOrgId: string;
  onClose: () => void;
}

export const MonitoringAgentsPopoverEmptyState = ({
  crmOrgId,
  onClose,
}: MonitoringAgentsPopoverEmptyStateProps) => {
  const {
    setLeadMonitoringLoading,
    setFlowMonitoringLoading,
    leadMonitoringLoading,
    flowMonitoringLoading,
  } = useMonitoringAgentHomepageContext();

  const onConfirmCreateAgent = useCreateAgent();
  const { get_metadataData } = useDocumentationApiFacade();
  const { sweepBestPractices } = useBestPracticesContent(
    AgentType.MonitoringV2,
    emptyStateFlowMonitoring(crmOrgId ?? ''),
    true,
  );

  const onLeadActivate = async () => {
    setLeadMonitoringLoading(true);
    await onConfirmCreateAgent({
      type: AgentType.MonitoringV2,
      name: `Lead Monitoring`,
      selectedContext: { objectType: 'Lead' },
    });
    setLeadMonitoringLoading(false);
  };

  const onFlowActivate = async () => {
    setFlowMonitoringLoading(true);
    const metadataData = (await get_metadataData('Flow')) ?? [];
    const contextComponents: MonitoringV2ContextComponent = {
      metadataType: 'Flow',
      includeConfigurations: metadataData
        .filter((metadata) => metadata.isActive)
        .map((metadata) => ({
          name: metadata.name,
          type: metadata.type,
        })),
    };
    const bestPracticeIds =
      sweepBestPractices
        ?.filter((bp) => bp.applicableResourceType === 'Flow')
        ?.map((bp) => bp.id) ?? [];

    await onConfirmCreateAgent({
      type: AgentType.MonitoringV2,
      name: `Flow Monitoring`,
      selectedContext: contextComponents,
      bestPracticeIds,
    });
    setFlowMonitoringLoading(false);
  };

  return (
    <>
      <Stack pb={1.5} pl={1}>
        <Typography variant="body-medium">
          No monitoring agents set up yet. Here are a few examples to help you get started:
        </Typography>
      </Stack>
      <AgentPopoverCard
        agent={emptyStateFlowMonitoring(crmOrgId ?? '')}
        objectLabel="All Objects"
        agentIssues={0}
        isActivating={flowMonitoringLoading}
        disableButton={leadMonitoringLoading}
        onActivate={async () => {
          await onFlowActivate();
          onClose();
        }}
      />
      <AgentPopoverCard
        agent={emptyStateLeadMonitoring(crmOrgId ?? '')}
        objectLabel="Lead"
        isLast={true}
        agentIssues={0}
        isActivating={leadMonitoringLoading}
        disableButton={flowMonitoringLoading}
        onActivate={async () => {
          await onLeadActivate();
          onClose();
        }}
      />
    </>
  );
};
